import { formatMoney, parseMoney } from "@hammer/core/money";
import { formatAge } from "@hammer/core/time";
import { FeedbackBox } from "./Feedback.tsx";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { BidChart } from "../components/BidChart.tsx";
import { absoluteTime, Price, sourceLabel, StanceControl, Thumb, TimeLeft } from "../components/lot-bits.tsx";
import { ResultForm } from "../components/ResultForm.tsx";
import { api, type LotPage } from "../lib/api.ts";
import { keys, useDeleteWithUndo, useLot, useSetStance, useStanceWithUndo } from "../lib/queries.ts";
import { refreshLot } from "../lib/refresh.ts";
import { toast, useNow } from "../lib/store.ts";

const REACH_NOTE: Record<LotPage["reach"], (label: string) => string> = {
  http: () => "Hammer checks this lot once a day at noon Eastern, and whenever you click Check now.",
  client: (l) => `${l} only answers browsers, so this lot updates once a day the first time Hammer is open after noon Eastern, or when you click the bookmarklet on the lot page.`,
  capture: (l) => `${l} refuses automated checks. Click the Hammer bookmarklet on the lot page to update it.`,
};

const ATTEMPT_LABEL: Record<string, string> = {
  ok: "OK", http_4xx: "Refused", http_5xx: "Site error", blocked: "Blocked", timeout: "Timed out", parse_error: "Couldn't read",
  not_found: "Gone", validation_failed: "Bad data", network: "Network error",
};

export function Lot() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useLot(id);
  const lot = q.data;
  const now = useNow();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setStance = useStanceWithUndo();
  const stanceM = useSetStance();
  const deleteLot = useDeleteWithUndo();
  const [img, setImg] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!lot || (e.target as HTMLElement).closest("input, textarea, select") || e.metaKey || e.ctrlKey || document.querySelector(".scrim")) return;
      if (e.key === "w") setStance(lot, "watching");
      else if (e.key === "b") setStance(lot, "bidding");
      else if (e.key === "p") setStance(lot, "passed");
      else if (e.key === "o") window.open(lot.url, "_blank", "noopener");
      else if (e.key === "Escape") void navigate({ to: "/" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lot, setStance, navigate]);

  if (q.isPending) return <div className="skeleton h-8 w-2/3" aria-busy />;
  if (!lot) return <p>That lot isn't in your list. <Link to="/" className="underline">Back to the agenda</Link></p>;

  const patch = async (body: Record<string, unknown>, done = "Saved") => {
    try {
      await api(`/lots/${lot.id}`, { method: "PATCH", body });
      toast(done);
    } catch (e) {
      toast(`Couldn't save: ${(e as Error).message}`, { tone: "error" });
    }
    await Promise.all([qc.invalidateQueries({ queryKey: keys.lot(lot.id) }), qc.invalidateQueries({ queryKey: keys.lots }), qc.invalidateQueries({ queryKey: keys.issues })]);
  };

  const archive = async () => {
    await patch({ archived: true }, "Archived");
    toast("Archived", { action: { label: "Undo", run: () => void patch({ archived: false }, "Restored") } });
    void navigate({ to: "/" });
  };

  const remove = () => {
    deleteLot(lot);
    void navigate({ to: "/" });
  };

  const label = sourceLabel(lot);
  const closedish = lot.phase === "closed" || lot.phase === "overtime";
  const checked = lot.freshness.lastOkAt ? now - Date.parse(lot.freshness.lastOkAt) : null;

  return (
    <article className="grid gap-6">
      <div>
        <Link to="/" className="muted text-[14px] no-underline hover:underline">Agenda</Link>
        <EditableTitle title={lot.title} onSave={(t) => patch({ title: t }, "Title saved")} />
        <p className="muted text-[14px] mt-1 flex flex-wrap gap-x-3">
          <a href={lot.url} target="_blank" rel="noreferrer noopener" className="underline underline-offset-2">Open on {label}</a>
          {lot.locationText && <span>{lot.locationText}</span>}
          {lot.lotNumber && <span>Lot {lot.lotNumber}</span>}
          {lot.auctionName && <span className="truncate max-w-[32rem]">{lot.auctionName}</span>}
        </p>
      </div>

      {lot.openIssues.length > 0 && (
        <ul className="grid gap-2">
          {lot.openIssues.map((i) => (
            <li key={i.id} className="attention !mb-0 justify-between">
              <span>{i.detail}</span>
              <button className="btn btn-quiet !min-h-0" onClick={async () => { await api(`/issues/${i.id}/resolve`, { method: "POST", body: { resolution: "dismissed" } }); void qc.invalidateQueries(); }}>Dismiss</button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] items-start">
        <div className="grid gap-8 min-w-0">
          {lot.images.length > 0 && (
            <div className="grid gap-2">
              <Thumb src={lot.images[img]} size="big" />
              {lot.images.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {lot.images.slice(0, 12).map((src, i) => (
                    <button key={src} className={`p-0 border-2 rounded ${i === img ? "border-ink" : "border-transparent"}`} onClick={() => setImg(i)} aria-label={`Photo ${i + 1}`}>
                      <img src={src} alt="" className="w-16 h-12 object-cover rounded-sm block" loading="lazy" referrerPolicy="no-referrer" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <section>
            <h2 className="heading text-[18px] mb-2">Bid history</h2>
            <BidChart observations={lot.observations} maxBidCents={lot.stance.maxBidCents} endAt={lot.latest?.endAt ?? null} />
          </section>

          {lot.description && (
            <section>
              <h2 className="heading text-[18px] mb-2">Description</h2>
              <p className="max-w-[70ch] whitespace-pre-line text-[14.5px]">{lot.description.slice(0, 2000)}</p>
            </section>
          )}

          <section>
            <h2 className="heading text-[18px] mb-2">Similar lots you've seen close</h2>
            {lot.comps.length ? (
              <table className="data">
                <thead><tr><th>Lot</th><th>Closed</th><th className="r">Sold for</th></tr></thead>
                <tbody>
                  {lot.comps.map((c) => (
                    <tr key={c.id}>
                      <td><Link to="/lot/$id" params={{ id: c.id }} className="underline underline-offset-2">{c.title}</Link></td>
                      <td className="muted">{c.closedAt ? absoluteTime(c.closedAt, now) : "—"}</td>
                      <td className="r num">{formatMoney(c.hammerCents)}{c.outcome === "won" ? " (you)" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted text-[14px]">None yet. Prices of closed lots with the same make and model{lot.tags.length ? " or tags" : ""} show up here.</p>
            )}
          </section>

          <section>
            <h2 className="heading text-[18px] mb-2">Checks</h2>
            <p className="muted text-[14px] mb-2">{REACH_NOTE[lot.reach](label)}</p>
            {lot.attempts.length ? (
              <table className="data">
                <thead><tr><th>When</th><th>Result</th><th className="r">Time</th></tr></thead>
                <tbody>
                  {lot.attempts.map((a) => (
                    <tr key={a.startedAt}>
                      <td className="muted">{absoluteTime(a.startedAt, now)}</td>
                      <td className={a.outcome === "ok" ? "" : "text-red"}>{ATTEMPT_LABEL[a.outcome] ?? a.outcome}{a.detail && a.outcome !== "ok" ? `: ${a.detail}` : ""}</td>
                      <td className="r num muted">{(a.durationMs / 1000).toFixed(1)}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="muted text-[14px]">No checks recorded yet.</p>}
            {lot.snapshot ? (
              <p className="text-[14px] mt-2">
                <a href={`/api/lots/${lot.id}/snapshot`} download className="underline underline-offset-2">Download the page Hammer last received</a>
                <span className="muted"> ({absoluteTime(lot.snapshot.at, now)}). If a price or time is wrong, send this file to whoever maintains Hammer.</span>
              </p>
            ) : null}
          </section>

          <details className="grid gap-2">
            <summary className="heading text-[18px] cursor-pointer">Something wrong? Tell Claude</summary>
            <div className="mt-2"><FeedbackBox lotId={lot.id} compact /></div>
          </details>
        </div>

        <aside className="panel grid gap-5 lg:sticky lg:top-6 order-first lg:order-none">
          <div className="flex justify-between items-start gap-4">
            <Price lot={lot} size="big" />
            <div className="text-right"><TimeLeft lot={lot} size="big" /></div>
          </div>
          <p className="text-[13px] muted -mt-2">
            {checked == null ? "Not checked yet." : `Checked ${formatAge(checked) === "just now" ? "just now" : `${formatAge(checked)} ago`}.`}{" "}
            {lot.phase !== "closed" && lot.reach === "http" ? "Hammer checks it once a day at noon Eastern." : ""}
          </p>
          {lot.reach !== "capture" && lot.phase !== "closed" && (
            <button className="btn" disabled={refreshing} onClick={async () => { setRefreshing(true); await refreshLot(qc, lot.id); setRefreshing(false); }}>
              {refreshing ? "Checking…" : "Check now"}
            </button>
          )}

          {closedish ? (
            <div className="grid gap-2">
              <h2 className="heading text-[18px]">How did it end?</h2>
              <ResultForm lot={lot} />
            </div>
          ) : (
            <div className="grid gap-3">
              <StanceControl value={lot.stance.stance} onChange={(s) => setStance(lot, s)} />
              {lot.stance.stance === "bidding" && (
                <MoneyField label="Your max bid" value={lot.stance.maxBidCents} onSave={(v) => stanceM.mutate({ id: lot.id, stance: "bidding", maxBidCents: v })} hint="Hammer tells you when the bid passes it." />
              )}
              {lot.stance.stance === "watching" && (
                <MoneyField label="Tell me if it's still under" value={lot.stance.alertUnderCents} onSave={(v) => stanceM.mutate({ id: lot.id, stance: "watching", alertUnderCents: v })} hint="Checked an hour before it closes." />
              )}
            </div>
          )}

          {lot.cost && (
            <div>
              <h2 className="heading text-[15px] mb-1.5">{lot.costBasis === "max" ? "If you win at your max" : "If you won at the current bid"}</h2>
              <dl className="kv num">
                <dt>Bid</dt><dd className="text-right">{formatMoney(lot.cost.bidCents)}</dd>
                <dt>Buyer's premium {lot.cost.premiumBps / 100}%{lot.cost.premiumKnown ? "" : " (assumed)"}</dt><dd className="text-right">{formatMoney(lot.cost.premiumCents)}</dd>
                {lot.cost.taxCents > 0 && <><dt>Sales tax</dt><dd className="text-right">{formatMoney(lot.cost.taxCents)}</dd></>}
                {lot.cost.feesCents > 0 && <><dt>Fees</dt><dd className="text-right">{formatMoney(lot.cost.feesCents)}</dd></>}
                {lot.cost.transportCents != null && <><dt>Transport, {lot.cost.miles} mi</dt><dd className="text-right">{formatMoney(lot.cost.transportCents)}</dd></>}
                <dt className="font-[650] !text-ink border-t border-rule pt-1">All in</dt><dd className="text-right board text-[18px] border-t border-rule pt-1">{formatMoney(lot.cost.totalCents)}</dd>
              </dl>
              {!lot.cost.premiumKnown && <p className="text-[12.5px] muted mt-1">Premium not found on the lot page. Set it below if you know it.</p>}
            </div>
          )}

          <TagsField tags={lot.tags} onSave={(tags) => patch({ tags }, "Tags saved")} />
          <NoteField value={lot.stance.note} onSave={(note) => stanceM.mutate({ id: lot.id, stance: lot.stance.stance, note })} />

          <details className="text-[14px]">
            <summary className="cursor-pointer muted">Correct details</summary>
            <div className="grid gap-3 mt-3">
              <MoneyField label="Current bid" value={lot.latest?.currentBidCents ?? null} onSave={(v) => patch({ currentBidCents: v })} />
              <label className="grid"><span className="label">End time</span>
                <input type="datetime-local" className="field" defaultValue={lot.latest?.endAt ? toLocalInput(lot.latest.endAt) : ""} onBlur={(e) => e.target.value && patch({ endAt: new Date(e.target.value).toISOString() })} />
              </label>
              <label className="grid"><span className="label">Buyer's premium, %</span>
                <input className="field num" inputMode="decimal" defaultValue={lot.buyerPremiumBps != null ? lot.buyerPremiumBps / 100 : ""} onBlur={(e) => { const n = Number(e.target.value); if (e.target.value !== "" && Number.isFinite(n)) void patch({ buyerPremiumBps: Math.round(n * 100) }); }} />
              </label>
            </div>
          </details>

          <div className="flex gap-2 flex-wrap border-t border-rule pt-4">
            {lot.archivedAt
              ? <button className="btn" onClick={() => void patch({ archived: false }, "Restored")}>Restore</button>
              : <button className="btn" onClick={archive}>Archive</button>}
            <button className="btn btn-danger" onClick={remove}>Delete</button>
          </div>
        </aside>
      </div>
    </article>
  );
}

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

function EditableTitle({ title, onSave }: { title: string; onSave: (t: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(title);
  useEffect(() => setV(title), [title]);
  if (!editing)
    return (
      <h1 className="heading text-[26px] leading-tight mt-1 max-w-[48rem]">
        {title}{" "}
        <button className="btn btn-quiet !min-h-0 !px-1 text-[13px] align-middle" onClick={() => setEditing(true)}>Edit</button>
      </h1>
    );
  return (
    <form className="flex gap-2 mt-1 max-w-[48rem]" onSubmit={(e) => { e.preventDefault(); if (v.trim()) onSave(v.trim()); setEditing(false); }}>
      <input autoFocus className="field text-[20px]" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setEditing(false)} aria-label="Title" />
      <button className="btn btn-primary">Save</button>
    </form>
  );
}

function MoneyField({ label, value, onSave, hint }: { label: string; value: number | null; onSave: (v: number | null) => void; hint?: string }) {
  const [v, setV] = useState(value != null ? String(value / 100) : "");
  useEffect(() => setV(value != null ? String(value / 100) : ""), [value]);
  const cents = v.trim() ? parseMoney(v) : null;
  const bad = v.trim() !== "" && cents == null;
  const commit = () => { if (!bad && cents !== value) onSave(cents); };
  return (
    <label className="grid">
      <span className="label">{label}</span>
      <input className="field num" inputMode="decimal" placeholder="$0" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && commit()} aria-invalid={bad} />
      {bad ? <span className="text-[12.5px] text-red mt-1">Enter an amount like 3800.</span> : hint && <span className="text-[12.5px] muted mt-1">{hint}</span>}
    </label>
  );
}

function TagsField({ tags, onSave }: { tags: string[]; onSave: (t: string[]) => void }) {
  const [v, setV] = useState("");
  const add = () => {
    const t = v.trim();
    if (t && !tags.includes(t)) onSave([...tags, t]);
    setV("");
  };
  return (
    <div>
      <span className="label">Tags</span>
      <div className="flex flex-wrap gap-1.5 items-center">
        {tags.map((t) => (
          <button key={t} className="chip" onClick={() => onSave(tags.filter((x) => x !== t))} aria-label={`Remove tag ${t}`}>{t} <span aria-hidden>×</span></button>
        ))}
        <input className="field !min-h-[30px] !py-0.5 w-32 text-[13px]" placeholder="Add a tag" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add} />
      </div>
    </div>
  );
}

function NoteField({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <label className="grid">
      <span className="label">Notes</span>
      <textarea className="field min-h-[4.5rem]" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== value && onSave(v)} placeholder="Condition, who to call, what it needs" />
    </label>
  );
}
